import React, { useCallback, useEffect, useState } from "react";
import { Link, Routes, Route, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import { supabase, getAccessToken } from "../lib/supabaseClient";
import { SBLogo } from "../components/WebLayout";
import Seo from "../components/Seo";
import { toast } from "sonner";
import {
  LayoutGrid, Store, ShoppingBag, Package, LogOut, Loader2, ArrowLeft,
  MapPin, Building2, DollarSign, Clock, Users, CheckCircle2, AlertCircle,
  ExternalLink, ChevronRight, Send, FileText, Mail, Phone, Zap, Sparkles,
} from "lucide-react";
import { formatEnum, formatBool, formatArray, formatOperatorRequestStatus, formatPaymentMethod } from "../lib/labels";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function af(path, opts = {}) {
  const token = await getAccessToken();
  const headers = { ...(opts.headers || {}), Authorization: `Bearer ${token || ""}` };
  return fetch(`${API}${path}`, { ...opts, headers });
}

const fmt$ = (c) => (c == null ? "—" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);

// -------------------- Shell --------------------
export default function OperatorPortal() {
  const nav = useNavigate();
  const loc = useLocation();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      // If a password recovery is active, force the reset screen instead of loading portal.
      try {
        if (sessionStorage.getItem("spotbounty_password_recovery_active") === "true") {
          window.location.replace("/auth/reset-password"); return;
        }
      } catch { /* ignore */ }
      const t = await getAccessToken();
      if (!t) { nav("/operator/login"); return; }
      const r = await af("/api/operator/me");
      if (r.status === 401 || r.status === 403) {
        if (r.status === 403) {
          const d = await r.json().catch(() => ({}));
          setMe({ blocked: true, reason: d?.detail || "not_authorized" });
          setChecking(false); return;
        }
        nav("/operator/login"); return;
      }
      if (r.ok) setMe(await r.json());
      setChecking(false);
    })();
  }, [nav]);

  async function signOut() {
    try { await supabase.auth.signOut(); } catch { /* ignore */ }
    nav("/operator/login");
  }

  if (checking) return <div className="min-h-screen bg-[#080B0D] flex items-center justify-center"><Loader2 className="animate-spin text-[#FFC107]" /></div>;
  if (me?.blocked) return <BlockedOperator reason={me.reason} onLogout={signOut} />;
  if (!me) return null;

  if (me.status !== "approved") {
    return (
      <BlockedOperator reason={
        me.status === "pending" ? "operator_pending" :
        me.status === "suspended" ? "operator_suspended" :
        me.status === "rejected" ? "operator_rejected" : "not_approved"} onLogout={signOut} me={me} />
    );
  }

  const path = loc.pathname;
  const NAV = [
    { key: "dashboard", label: "Dashboard", icon: LayoutGrid, to: "/operator/dashboard" },
    { key: "opps", label: "Opportunities", icon: Store, to: "/operator/opportunities" },
    { key: "requests", label: "My Requests", icon: ShoppingBag, to: "/operator/requests" },
    { key: "purchased", label: "Purchased", icon: Package, to: "/operator/purchased" },
  ];

  return (
    <div className="min-h-screen bg-[#080B0D] text-white flex flex-col">
      <Seo title="Operator Portal" description="Browse and purchase vending opportunities" path={path} noindex />
      <header className="border-b border-[#2A2F34] sticky top-0 bg-[#080B0D] z-30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><SBLogo /></Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11.5px] text-[#A1A1AA]">{me.company || me.name}</span>
            <button onClick={signOut} className="text-[11.5px] text-[#A1A1AA] hover:text-white flex items-center gap-1" data-testid="op-logout">
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
        <nav className="border-t border-[#2A2F34] bg-[#0B0E11]">
          <div className="max-w-7xl mx-auto px-4 md:px-6 flex gap-1 overflow-x-auto">
            {NAV.map((n) => {
              const active = path.startsWith(n.to);
              return (
                <Link key={n.key} to={n.to}
                      className={`px-3 py-3 text-[12px] flex items-center gap-2 whitespace-nowrap border-b-2 ${active ? "border-[#FFC107] text-white" : "border-transparent text-[#A1A1AA] hover:text-white"}`}
                      data-testid={`op-nav-${n.key}`}>
                  <n.icon size={12} /> {n.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </header>
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 md:px-6 py-6">
        <Routes>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard me={me} />} />
          <Route path="opportunities" element={<OpsList />} />
          <Route path="opportunities/:id" element={<OpDetail />} />
          <Route path="requests" element={<Requests />} />
          <Route path="purchased" element={<Purchased />} />
          <Route path="purchased/:id" element={<Package_ />} />
        </Routes>
      </main>
    </div>
  );
}

function BlockedOperator({ reason, onLogout, me }) {
  const M = {
    operator_pending: { title: "Awaiting Approval", body: "Your account is pending SpotBounty review. Marketplace access unlocks once approved. We’ll email you when a decision is made." },
    operator_suspended: { title: "Account Suspended", body: "Your operator access has been suspended. Please contact support to review the situation." },
    operator_rejected: { title: "Application Not Approved", body: "Your operator application was not approved at this time. Please contact support if you have questions." },
    email_not_verified: { title: "Verify Your Email", body: "Please click the link in the confirmation email we sent to enable marketplace access." },
    not_an_operator: { title: "No Operator Profile Yet", body: "Complete your operator signup to browse the marketplace." },
    not_authorized: { title: "Not Authorized", body: "You don’t have access to the operator marketplace." },
  }[reason] || { title: "Not Available", body: "Marketplace access is unavailable." };
  return (
    <div className="min-h-screen bg-[#080B0D] text-white flex items-center justify-center p-6">
      <div className="sb-card p-8 max-w-md w-full" data-testid="op-blocked">
        <div className="text-[#F59E0B] flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold"><AlertCircle size={14} /> Status</div>
        <h1 className="mt-3 text-[24px] font-black">{M.title}</h1>
        <p className="mt-3 text-[13px] text-[#A1A1AA] leading-relaxed">{M.body}</p>
        {me && <div className="mt-4 text-[11px] text-[#52525b]">Signed in as {me.email}</div>}
        <button onClick={onLogout} className="mt-6 sb-btn-secondary text-[11px]" style={{ padding: "8px 14px" }}>Sign out</button>
      </div>
    </div>
  );
}

// -------------------- Dashboard --------------------
function Dashboard({ me }) {
  const [stats, setStats] = useState({ available: 0, requests: 0, purchased: 0 });
  useEffect(() => {
    (async () => {
      const [a, r, p] = await Promise.all([
        af("/api/operator/opportunities").then(x => x.ok ? x.json() : { rows: [] }),
        af("/api/operator/requests").then(x => x.ok ? x.json() : { rows: [] }),
        af("/api/operator/purchased").then(x => x.ok ? x.json() : { rows: [] }),
      ]);
      setStats({ available: a.rows?.length || 0, requests: r.rows?.length || 0, purchased: p.rows?.length || 0 });
    })();
  }, []);
  return (
    <div className="space-y-5" data-testid="op-dashboard">
      <div>
        <div className="sb-eyebrow">Welcome</div>
        <h1 className="mt-2 text-[28px] font-black">Hello, {me.name?.split(" ")[0] || "Operator"}.</h1>
        <p className="mt-1 text-[13px] text-[#A1A1AA]">Pre-verified vending opportunities are ready.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <Stat label="Available Opportunities" v={stats.available} icon={Store} to="/operator/opportunities" testid="op-stat-avail" />
        <Stat label="My Requests" v={stats.requests} icon={ShoppingBag} to="/operator/requests" testid="op-stat-req" />
        <Stat label="Purchased" v={stats.purchased} icon={Package} to="/operator/purchased" testid="op-stat-purch" />
      </div>
    </div>
  );
}

function Stat({ label, v, icon: Icon, to, testid }) {
  return (
    <Link to={to} className="sb-card p-5 hover:border-[#FFC107] transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between"><Icon size={16} className="text-[#FFC107]" /><ChevronRight size={14} className="text-[#52525b]" /></div>
      <div className="mt-3 text-[36px] font-black">{v}</div>
      <div className="text-[11px] uppercase tracking-widest text-[#A1A1AA] font-bold mt-1">{label}</div>
    </Link>
  );
}

// -------------------- Opportunities list --------------------
function OpsList() {
  const [rows, setRows] = useState(null);
  useEffect(() => { af("/api/operator/opportunities").then(r => r.ok ? r.json() : { rows: [] }).then(d => setRows(d.rows || [])); }, []);
  if (rows == null) return <Loader />;
  return (
    <div className="space-y-4">
      <div><div className="sb-eyebrow">Marketplace</div>
        <h1 className="mt-2 text-[24px] font-black">Available Opportunities</h1>
        <p className="text-[13px] text-[#A1A1AA]">{rows.length} live opportunit{rows.length === 1 ? "y" : "ies"}. Details unlock on purchase.</p></div>
      {rows.length === 0 ? (
        <div className="sb-card p-6 text-center text-[#A1A1AA] text-[13px]" data-testid="op-empty">No opportunities available right now. Check back soon.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {rows.map((r) => (
            <Link key={r.id} to={`/operator/opportunities/${r.id}`} className="sb-card p-5 hover:border-[#FFC107] transition-colors" data-testid={`op-card-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="sb-pill sb-pill-gold text-[10px]">{formatEnum(r.business_type) || "Vending"}</div>
                  <h3 className="mt-2 text-[16px] font-black">{r.listing_title}</h3>
                  <div className="text-[12px] text-[#A1A1AA] mt-1 flex items-center gap-1"><MapPin size={11} /> {r.public_city}, {r.public_state}</div>
                </div>
                <div className="text-right"><div className="text-[20px] font-black text-[#FFC107]">{fmt$(r.asking_price_cents)}</div></div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-[#A1A1AA]">
                {r.employee_count != null && <span><Users size={10} className="inline mr-1" />{r.employee_count} employees</span>}
                {r.open_24_7 && <span><Clock size={10} className="inline mr-1" />24/7</span>}
                {r.number_of_shifts && <span>{r.number_of_shifts} shifts</span>}
              </div>
              {r.financial_terms_summary && <div className="mt-2 text-[11.5px] text-[#A1A1AA]"><DollarSign size={11} className="inline" /> {r.financial_terms_summary}</div>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Loader() { return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#FFC107]" /></div>; }

// -------------------- Opportunity Detail --------------------
function OpDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null); const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    const r = await af(`/api/operator/opportunities/${id}`);
    if (r.status === 410) { const j = await r.json().catch(() => ({})); setErr("already_purchased"); return; }
    if (!r.ok) { setErr("not_found"); return; }
    setD(await r.json());
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function request() {
    setBusy(true);
    const r = await af(`/api/operator/opportunities/${id}/request-purchase`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
    setBusy(false); setConfirm(false);
    if (!r.ok) { const j = await r.json().catch(() => ({})); toast.error(j?.detail || "Request failed"); return; }
    toast.success("Purchase request submitted");
    nav("/operator/requests");
  }

  if (err === "already_purchased") return <div className="sb-card p-6 text-center"><div className="text-[#22C55E] text-[13px]">You already own this opportunity.</div><Link to="/operator/purchased" className="sb-btn-primary text-[11px] mt-4" style={{ padding: "8px 14px" }}>Go to your Purchased Locations</Link></div>;
  if (err) return <div className="sb-card p-6 text-center text-[#A1A1AA]">Opportunity not found.</div>;
  if (!d) return <Loader />;

  const already = d.my_request_status && ["pending","approved","payment_pending","paid"].includes(d.my_request_status);

  return (
    <div className="space-y-4" data-testid="op-detail">
      <Link to="/operator/opportunities" className="text-[12px] text-[#A1A1AA] hover:text-white flex items-center gap-1"><ArrowLeft size={12} /> Back to marketplace</Link>
      <div className="grid lg:grid-cols-[1fr_360px] gap-4">
        <div className="space-y-4">
          <div className="sb-card p-5">
            <div className="sb-pill sb-pill-gold text-[10px]">{formatEnum(d.business_type) || "Vending"}</div>
            <h1 className="mt-3 text-[26px] font-black">{d.listing_title}</h1>
            <div className="text-[13px] text-[#A1A1AA] mt-1"><MapPin size={12} className="inline mr-1" />{d.public_city}, {d.public_state}{d.approximate_area && ` · ${d.approximate_area}`}</div>
            {d.sanitized_description && <p className="mt-4 text-[13.5px] leading-relaxed text-[#D4D4D8]">{d.sanitized_description}</p>}
          </div>
          {d.photos?.length > 0 && (
            <div className="sb-card p-4">
              <div className="sb-eyebrow">Preview Photos</div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
                {d.photos.map((p) => (
                  <div key={p.id} className="aspect-video overflow-hidden rounded border border-[#2A2F34]">
                    <img src={p.signed_url} alt={p.caption || ""} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <KV k="Traffic" v={d.traffic_summary} />
            <KV k="Employees" v={d.employee_count} />
            <KV k="Visitors / day" v={d.visitors_per_day} />
            <KV k="Shifts" v={d.number_of_shifts} />
            <KV k="Hours" v={d.operating_hours || (d.open_24_7 ? "24 / 7" : null)} />
            <KV k="Foot Traffic" v={formatEnum(d.foot_traffic_tier)} />
            <KV k="Existing Vending" v={d.current_vending_summary} />
            <KV k="Machine Types" v={formatArray(d.machine_types)} />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <KV k="Placement" v={d.placement_summary} />
            <KV k="Financial Terms" v={d.financial_terms_summary} />
            <KV k="Agreement Term" v={d.agreement_term_months ? `${d.agreement_term_months} months` : null} />
            <KV k="Exclusivity" v={d.exclusivity_summary} />
          </div>
        </div>
        <aside className="space-y-3">
          <div className="sb-card p-5 border-[#FFC107]/40 sticky top-32">
            <div className="sb-eyebrow text-[#FFC107]">Asking Price</div>
            <div className="mt-2 text-[32px] font-black text-[#FFC107]">{fmt$(d.asking_price_cents)}</div>
            <div className="mt-2 text-[11px] text-[#A1A1AA]">One-time. No SpotBounty revenue commission.</div>
            {already ? (
              <div className="mt-5 text-[12px] text-[#22C55E] bg-[#22C55E]/10 border border-[#22C55E]/30 rounded p-3">
                You already have a <b>{d.my_request_status}</b> request. See your <Link to="/operator/requests" className="underline">Requests</Link>.
              </div>
            ) : (
              <button onClick={() => setConfirm(true)} disabled={busy} className="mt-5 sb-btn-primary w-full text-[12px]" style={{ padding: "12px 14px" }} data-testid="op-request-btn">
                <Send size={12} /> Request to Purchase
              </button>
            )}
            <div className="mt-4 text-[11px] text-[#A1A1AA] leading-relaxed">
              Details unlock after admin approves your request and payment is confirmed. Payment is handled externally by SpotBounty admin.
            </div>
          </div>
        </aside>
      </div>
      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setConfirm(false)}>
          <div className="sb-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[18px] font-black">Submit purchase request?</h3>
            <p className="mt-2 text-[13px] text-[#A1A1AA]">Locks the asking price of <b className="text-[#FFC107]">{fmt$(d.asking_price_cents)}</b> for your request. SpotBounty admin reviews. If approved, you’ll receive payment instructions and unlock the buyer package.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConfirm(false)} className="sb-btn-secondary text-[11px]" style={{ padding: "9px 14px" }}>Cancel</button>
              <button onClick={request} className="sb-btn-primary text-[11px]" style={{ padding: "9px 14px" }} data-testid="op-confirm-req">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- Requests --------------------
function Requests() {
  const [rows, setRows] = useState(null);
  useEffect(() => { af("/api/operator/requests").then(r => r.ok ? r.json() : { rows: [] }).then(d => setRows(d.rows || [])); }, []);
  if (rows == null) return <Loader />;
  const P = { pending: "amber", approved: "amber", payment_pending: "amber", paid: "green", rejected: "red", cancelled: "gray", expired: "gray" };
  return (
    <div className="space-y-4" data-testid="op-requests">
      <div><div className="sb-eyebrow">My Requests</div>
        <h1 className="mt-2 text-[24px] font-black">Purchase Requests</h1></div>
      {rows.length === 0 ? (
        <div className="sb-card p-6 text-center text-[#A1A1AA] text-[13px]">No requests yet.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="sb-card p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="text-[15px] font-bold">{r.listing_title}</h3>
                  <div className="text-[12px] text-[#A1A1AA]"><MapPin size={11} className="inline" /> {r.city}, {r.state}</div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`sb-pill sb-pill-${P[r.status] || "gray"}`}>{formatOperatorRequestStatus(r.status)}</span>
                  <span className="text-[15px] font-black text-[#FFC107]">{fmt$(r.price_cents)}</span>
                </div>
              </div>
              <div className="mt-2 text-[11px] text-[#A1A1AA]">
                Requested {new Date(r.requested_at).toLocaleString()}
                {r.approved_at && ` · Approved ${new Date(r.approved_at).toLocaleString()}`}
                {r.paid_at && ` · Unlocked ${new Date(r.paid_at).toLocaleString()}`}
              </div>
              {r.status === "paid" && (
                <Link to={`/operator/purchased/${r.opportunity_id}`} className="mt-3 inline-block text-[11px] text-[#FFC107] hover:underline">
                  View Buyer Package →
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -------------------- Purchased list --------------------
function Purchased() {
  const [rows, setRows] = useState(null);
  useEffect(() => { af("/api/operator/purchased").then(r => r.ok ? r.json() : { rows: [] }).then(d => setRows(d.rows || [])); }, []);
  if (rows == null) return <Loader />;
  return (
    <div className="space-y-4" data-testid="op-purchased">
      <div><div className="sb-eyebrow">Purchased</div>
        <h1 className="mt-2 text-[24px] font-black">Your Locations</h1></div>
      {rows.length === 0 ? (
        <div className="sb-card p-6 text-center text-[#A1A1AA] text-[13px]">You haven’t purchased any opportunities yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {rows.map((r) => (
            <Link key={r.id} to={`/operator/purchased/${r.opportunity_id}`} className="sb-card p-5 hover:border-[#FFC107]" data-testid={`op-purch-${r.opportunity_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-[16px] font-black">{r.listing_title}</h3>
                  <div className="text-[12px] text-[#A1A1AA] mt-1"><MapPin size={11} className="inline" /> {r.city}, {r.state}</div>
                  <div className="text-[11px] text-[#A1A1AA] mt-1">Purchased {new Date(r.assigned_at).toLocaleDateString()}</div>
                </div>
                <span className="text-[15px] font-black text-[#FFC107]">{fmt$(r.sale_price_cents)}</span>
              </div>
              <div className="mt-3 text-[11px] text-[#22C55E] flex items-center gap-1"><CheckCircle2 size={11} /> Package unlocked</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// -------------------- Buyer Package --------------------
function Package_() {
  const { id } = useParams();
  const [d, setD] = useState(null); const [err, setErr] = useState(null);
  useEffect(() => {
    af(`/api/operator/purchased/${id}/package`).then(async r => {
      if (!r.ok) { setErr(r.status); return; }
      setD(await r.json());
    });
  }, [id]);
  if (err === 404) return <div className="sb-card p-6 text-center text-[#A1A1AA]">Not found.</div>;
  if (!d) return <Loader />;
  const L = d.location, DM = d.decision_maker, P = d.placement, T = d.traffic, E = d.equipment, A = d.agreement;
  return (
    <div className="space-y-4" data-testid="op-package">
      <Link to="/operator/purchased" className="text-[12px] text-[#A1A1AA] hover:text-white flex items-center gap-1"><ArrowLeft size={12} /> Back</Link>
      <div className="sb-card p-5 border-[#22C55E]/40" style={{ background: "rgba(34,197,94,.05)" }}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[#22C55E] flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold"><CheckCircle2 size={12} /> Buyer Package Unlocked</div>
            <h1 className="mt-2 text-[26px] font-black">{L.business_name}</h1>
            <div className="text-[13px] text-[#A1A1AA]">{L.address_line1}{L.suite_unit ? ` · ${L.suite_unit}` : ""}, {L.city}, {L.state} {L.zip}</div>
          </div>
          <div className="text-right"><div className="sb-eyebrow">Paid</div><div className="text-[22px] font-black text-[#FFC107]">{fmt$(d.sale_price_cents)}</div></div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="sb-card p-5">
          <div className="sb-eyebrow">Decision Maker</div>
          <div className="mt-2 text-[16px] font-bold">{DM.full_name || "—"}</div>
          <div className="text-[12px] text-[#A1A1AA]">{DM.job_title || ""}</div>
          {DM.email && <a href={`mailto:${DM.email}`} className="mt-2 text-[13px] flex items-center gap-2 text-[#FFC107] hover:underline"><Mail size={12} />{DM.email}</a>}
          {DM.phone && <a href={`tel:${DM.phone}`} className="text-[13px] flex items-center gap-2 text-[#FFC107] hover:underline"><Phone size={12} />{DM.phone}</a>}
          {DM.best_contact_time && <div className="mt-1 text-[11.5px] text-[#A1A1AA]">Best contact: {DM.best_contact_time}</div>}
        </div>

        <div className="sb-card p-5">
          <div className="sb-eyebrow">Location</div>
          <div className="mt-2 text-[13px] space-y-1">
            <div><b>Business Type:</b> {formatEnum(L.business_type)}</div>
            {L.google_maps_address && <a href={L.google_maps_address} target="_blank" rel="noopener noreferrer" className="text-[#FFC107] text-[12px] flex items-center gap-1 hover:underline"><ExternalLink size={11} /> Open in Google Maps</a>}
          </div>
        </div>
      </div>

      {d.payment_confirmed && (
        <div className="sb-card p-5" data-testid="op-payment-confirmed">
          <div className="sb-eyebrow text-[#22C55E]">Payment Confirmed</div>
          <div className="mt-2 grid md:grid-cols-3 gap-3 text-[13px]">
            <KV k="Amount Received" v={fmt$(d.payment_confirmed.amount_received_cents)} />
            <KV k="Payment Method" v={formatPaymentMethod(d.payment_confirmed.payment_method)} />
            <KV k="Received At" v={d.payment_confirmed.payment_received_at ? new Date(d.payment_confirmed.payment_received_at).toLocaleString() : "—"} />
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        <Panel title="Traffic & Operations">
          <KV k="Employees" v={T.employees ?? "—"} /><KV k="Residents" v={T.residents ?? "—"} />
          <KV k="Visitors / day" v={T.visitors_per_day ?? "—"} /><KV k="Shifts" v={T.shifts ?? "—"} />
          <KV k="Hours" v={T.hours} /><KV k="24 / 7" v={formatBool(T.open_24_7)} />
          <KV k="Weekend Activity" v={formatEnum(T.weekend_activity)} /><KV k="Foot Traffic" v={formatEnum(T.foot_traffic_tier)} />
        </Panel>
        <Panel title="Placement Details">
          <KV k="Description" v={P.exact_placement_description} wide />
          <KV k="Outlet Available" v={formatBool(P.outlet_available)} />
          <KV k="Outlet Notes" v={P.outlet_notes} wide />
          <KV k="Width (in)" v={P.available_width_in ?? "—"} /><KV k="Depth (in)" v={P.available_depth_in ?? "—"} />
          <KV k="Wi-Fi" v={formatEnum(P.wifi_available)} /><KV k="Cellular" v={formatEnum(P.cellular_signal)} />
          <KV k="Indoor / Outdoor" v={formatEnum(P.indoor_outdoor)} /><KV k="Secure After Hours" v={formatBool(P.secure_after_hours)} />
        </Panel>
      </div>

      <Panel title="Equipment & Vending">
        <KV k="Preferred Machine Types" v={formatArray(E.preferred_machine_types)} wide />
        <KV k="Existing Vending" v={formatBool(E.existing_vending_state)} />
        <KV k="Existing Machine Types" v={formatArray(E.existing_machine_types)} />
        <KV k="Existing Condition" v={formatEnum(E.existing_machine_condition)} />
        {E.existing_notes && <KV k="Existing Notes" v={E.existing_notes} wide />}
      </Panel>

      {A && (
        <Panel title="Agreement">
          <KV k="Reference" v={A.reference_number} />
          <KV k="Signed at" v={A.signed_at ? new Date(A.signed_at).toLocaleString() : "—"} />
          <KV k="Term" v={A.term_months ? `${A.term_months} months` : (A.term_custom || "—")} />
          <KV k="Financial" v={A.financial_terms_summary} wide />
          <KV k="Exclusivity" v={A.exclusivity_summary} />
          {d.signed_agreement_access?.warning && (
            <div className="col-span-2 mt-2 border-2 border-[#F59E0B] rounded p-3 text-[12px] text-[#F59E0B] bg-[#F59E0B]/5 flex items-start gap-2">
              <AlertCircle size={13} className="mt-0.5" /><span>{d.signed_agreement_access.warning}</span>
            </div>
          )}
        </Panel>
      )}

      {d.photos?.length > 0 && (
        <div className="sb-card p-4">
          <div className="sb-eyebrow">Photos</div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2">
            {d.photos.map((p) => (<div key={p.id} className="aspect-video overflow-hidden rounded border border-[#2A2F34]"><img src={p.signed_url} alt={p.caption || ""} className="w-full h-full object-cover" /></div>))}
          </div>
        </div>
      )}
    </div>
  );
}

function KV({ k, v, wide }) {
  if (v == null || v === "" || v === "—") return null;
  return <div className={wide ? "md:col-span-2" : ""}><div className="text-[10.5px] uppercase tracking-widest text-[#A1A1AA] font-bold">{k}</div><div className="text-[13px] mt-0.5">{String(v)}</div></div>;
}

function Panel({ title, children }) {
  return (<div className="sb-card p-5"><div className="sb-eyebrow">{title}</div><div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">{children}</div></div>);
}
